import { ChevronRightIcon, PenIcon } from "@xola/icons";
import { Badge, Checkbox, Input, Label, Table } from "@xola/ui-kit";
import { SecondaryButton } from "common";
import { useFormikContext } from "formik";
import pluralize from "pluralize";
import React, { useCallback, useMemo } from "react";
import { Merchandise, MerchandiseCatalogItemStatus } from "types";
import { MerchandiseItemsFormValues } from "../merchandiseHelper";
import { getChildren, getLeafDescendants, VariantCatalogItem } from "./VariantTableHelper";
import { CatalogItemImage } from "@/components/Common/CatalogItemImage";
import { ProductImage } from "@/components/Common/ProductImage";
import { CurrencyInput } from "@/components/Input/CurrencyInput";

interface VariantNameCellProps {
    item: VariantCatalogItem;
    isLeaf: boolean;
    isDraft: boolean;
    variantCount: number;
}

const VariantNameCell = ({ item, isLeaf, isDraft: isNew, variantCount }: VariantNameCellProps) => {
    return (
        <div className="flex flex-col">
            <span className="whitespace-normal break-words font-bold">{item.variantOptionName ?? item.name}</span>
            {isNew && (
                <div className="mt-1 flex space-x-1">
                    <Badge color="primary" size="small">
                        Draft
                    </Badge>
                </div>
            )}
            {!isLeaf && variantCount > 0 && (
                <span className="text-sm text-gray-dark">
                    {variantCount} {pluralize("variant", variantCount)}
                </span>
            )}
        </div>
    );
};

// --- Isolated cell components: each owns its own Formik subscription ---

interface VariantSkuCellProps {
    itemId: string;
    fallbackSku: string;
    hasDuplicateSku: boolean;
}

const VariantSkuCell = React.memo(({ itemId, fallbackSku, hasDuplicateSku }: VariantSkuCellProps) => {
    const { values, setFieldValue, submitCount } = useFormikContext<MerchandiseItemsFormValues>();
    const skuValue = values.variantItems?.[itemId]?.sku ?? fallbackSku;
    const isRequired = submitCount > 0 && skuValue.trim().length === 0;
    const hasSkuError = isRequired || hasDuplicateSku;

    const handleChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            void setFieldValue(`variantItems.${itemId}.sku`, event.target.value, false);
        },
        [itemId, setFieldValue],
    );

    return (
        <Table.Cell data-testid="variant-item-sku">
            <div className="space-y-1">
                <Input isError={hasSkuError} value={skuValue} placeholder="Add SKU" onChange={handleChange} />
                {isRequired && <Label className="!mb-0 text-xs !text-danger">SKU is required</Label>}
                {hasDuplicateSku && <Label className="!mb-0 text-xs !text-danger">SKU is duplicated</Label>}
            </div>
        </Table.Cell>
    );
});

interface VariantBarcodeCellProps {
    itemId: string;
    fallbackBarcode: string;
}

const VariantBarcodeCell = React.memo(({ itemId, fallbackBarcode }: VariantBarcodeCellProps) => {
    const { values, setFieldValue } = useFormikContext<MerchandiseItemsFormValues>();
    const barcodeValue = values.variantItems?.[itemId]?.barcode ?? fallbackBarcode;

    const handleChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            void setFieldValue(`variantItems.${itemId}.barcode`, event.target.value, false);
        },
        [itemId, setFieldValue],
    );

    return (
        <Table.Cell data-testid="variant-item-barcode">
            <Input value={barcodeValue} placeholder="Add Barcode" onChange={handleChange} />
        </Table.Cell>
    );
});

interface VariantPriceCellProps {
    itemId: string;
    fallbackPrice: number | null | undefined;
}

const VariantPriceCell = React.memo(({ itemId, fallbackPrice }: VariantPriceCellProps) => {
    const { values, setFieldValue, submitCount } = useFormikContext<MerchandiseItemsFormValues>();
    const draftPrice = values.variantItems?.[itemId]?.price;
    const priceValue = draftPrice !== undefined ? draftPrice : fallbackPrice;
    const hasPriceError = submitCount > 0 && priceValue == null && fallbackPrice != null;

    const handleChange = useCallback(
        ({ floatValue }: { floatValue?: number }) => {
            const nextValue = floatValue ?? null;
            // When value matches the persisted price and no draft exists, this is a
            // programmatic fire from react-number-format — skip
            // to avoid falsely dirtying the Formik form.
            if (nextValue === fallbackPrice && draftPrice === undefined) {
                return;
            }
            void setFieldValue(`variantItems.${itemId}.price`, nextValue, false);
        },
        [draftPrice, fallbackPrice, itemId, setFieldValue],
    );

    return (
        <Table.Cell>
            <div className="w-25 space-y-1">
                <CurrencyInput
                    allowNegative={false}
                    value={priceValue}
                    isError={hasPriceError}
                    onValueChange={handleChange}
                />
                {hasPriceError && <Label className="!mb-0 text-xs !text-danger">Price is required</Label>}
            </div>
        </Table.Cell>
    );
});

interface VariantInventoryRowProps {
    item: VariantCatalogItem;
    depth?: number;
    merchandise: Merchandise;
    tree: Map<string | null, VariantCatalogItem[]>;
    expandedItems: Set<string>;
    setExpandedItems: React.Dispatch<React.SetStateAction<Set<string>>>;
    selectedItems: Set<string>;
    toggleSelectItem: (itemId: string) => void;
    toggleGroupItems: (leafIds: string[]) => void;
    setEditingItem: (item: VariantCatalogItem) => void;
    duplicateSkuItemIds: Set<string>;
    isInventoryTracked?: boolean;
}

export const VariantInventoryRow = React.memo(
    ({
        item,
        depth = 0,
        merchandise,
        tree,
        expandedItems,
        setExpandedItems,
        selectedItems,
        toggleSelectItem,
        toggleGroupItems,
        setEditingItem,
        duplicateSkuItemIds,
        isInventoryTracked = false,
    }: VariantInventoryRowProps) => {
        const itemId = item.id ?? "";
        const children = getChildren(tree, itemId);
        const hasChildren = children.length > 0;
        const isLeaf = item.isLeaf ?? !hasChildren;
        const isDraft = isLeaf && item.status === MerchandiseCatalogItemStatus.DRAFT;
        const paddingLeft = depth * 32;
        const isExpanded = expandedItems.has(itemId);

        const leafDescendantIds = useMemo(
            () => (isLeaf ? [] : getLeafDescendants(itemId, tree)),
            [isLeaf, itemId, tree],
        );
        const variantCount = leafDescendantIds.length;
        const isGroupChecked = leafDescendantIds.length > 0 && leafDescendantIds.every((id) => selectedItems.has(id));
        const isGroupIndeterminate = !isGroupChecked && leafDescendantIds.some((id) => selectedItems.has(id));
        const handleToggleGroup = useCallback(
            () => toggleGroupItems(leafDescendantIds),
            [leafDescendantIds, toggleGroupItems],
        );

        const handleToggle = useCallback(
            (e: React.MouseEvent) => {
                e.stopPropagation();
                setExpandedItems((previous) => {
                    const next = new Set(previous);
                    if (next.has(itemId)) {
                        next.delete(itemId);
                    } else {
                        next.add(itemId);
                    }
                    return next;
                });
            },
            [itemId, setExpandedItems],
        );

        const handleEdit = useCallback(
            (e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                setEditingItem(item);
            },
            [item, setEditingItem],
        );

        const handleToggleSelect = useCallback(() => toggleSelectItem(itemId), [itemId, toggleSelectItem]);

        return (
            <React.Fragment key={itemId}>
                <Table.Row className="border-b border-gray-light hover:bg-gray-lighter">
                    <Table.Cell data-testid="variant-item-name">
                        <div className="flex items-center" style={{ paddingLeft: `${paddingLeft}px` }}>
                            {hasChildren ? (
                                <span
                                    className="mr-2 flex h-5 w-5 cursor-pointer items-center justify-center"
                                    onClick={handleToggle}
                                >
                                    <ChevronRightIcon
                                        size="small"
                                        className={`transition-transform duration-200 ${isExpanded ? "rotate-90" : ""}`}
                                    />
                                </span>
                            ) : (
                                <span className="mr-2 w-5" />
                            )}
                            {isLeaf ? (
                                item.photo ? (
                                    <CatalogItemImage catalogItem={item} size="small" className="mr-2" />
                                ) : (
                                    <ProductImage product={merchandise} size="small" className="mr-2" />
                                )
                            ) : null}
                            <VariantNameCell
                                item={item}
                                isLeaf={isLeaf}
                                isDraft={isDraft}
                                variantCount={variantCount}
                            />
                        </div>
                    </Table.Cell>

                    {isLeaf ? (
                        <VariantSkuCell
                            itemId={itemId}
                            fallbackSku={item.sku ?? ""}
                            hasDuplicateSku={duplicateSkuItemIds.has(itemId)}
                        />
                    ) : (
                        <Table.Cell data-testid="variant-item-sku" />
                    )}

                    {isLeaf ? (
                        <VariantBarcodeCell itemId={itemId} fallbackBarcode={item.barcode ?? ""} />
                    ) : (
                        <Table.Cell data-testid="variant-item-barcode" />
                    )}

                    {isLeaf ? (
                        <VariantPriceCell itemId={itemId} fallbackPrice={item.prices?.perUnit?.price ?? item.price} />
                    ) : (
                        <Table.Cell />
                    )}

                    {isInventoryTracked &&
                        (isLeaf ? (
                            <Table.Cell data-testid="variants-item-quantity" className="text-center">
                                <span className="text-sm">{item.available ?? 0}</span>
                            </Table.Cell>
                        ) : (
                            <Table.Cell />
                        ))}

                    <Table.Cell data-testid="variant-item-edit-button">
                        {isLeaf ? (
                            <div className="flex items-center space-x-2">
                                <SecondaryButton type="button" size="small" onClick={handleEdit}>
                                    <PenIcon size="small" />
                                </SecondaryButton>
                                <Checkbox label="" checked={selectedItems.has(itemId)} onChange={handleToggleSelect} />
                            </div>
                        ) : (
                            <div className="flex justify-end">
                                <Checkbox
                                    label=""
                                    checked={isGroupChecked}
                                    indeterminate={isGroupIndeterminate || undefined}
                                    onChange={handleToggleGroup}
                                />
                            </div>
                        )}
                    </Table.Cell>
                </Table.Row>

                {hasChildren &&
                    isExpanded &&
                    children.map((child) => (
                        <VariantInventoryRow
                            key={child.id}
                            item={child}
                            depth={depth + 1}
                            merchandise={merchandise}
                            tree={tree}
                            expandedItems={expandedItems}
                            setExpandedItems={setExpandedItems}
                            selectedItems={selectedItems}
                            toggleSelectItem={toggleSelectItem}
                            toggleGroupItems={toggleGroupItems}
                            setEditingItem={setEditingItem}
                            duplicateSkuItemIds={duplicateSkuItemIds}
                            isInventoryTracked={isInventoryTracked}
                        />
                    ))}
            </React.Fragment>
        );
    },
);
